import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Pagination from '@/components/base/Pagination';
import ConfirmModal from '@/components/base/ConfirmModal';

const basePath = (__BASE_PATH__ || '/').replace(/\/$/, '');

interface Article {
  id: number;
  title_zh: string;
  title_en: string;
  title_ru: string;
  category_id: number;
  status: string;
  tags: string[];
  read_time: string;
  cover_img: string;
  featured: boolean;
  sort_order: number;
  published_at: string;
  scheduled_at: string;
  updated_at: string;
  created_at: string;
  article_categories?: { name_zh: string };
}

interface Category {
  id: number;
  name_zh: string;
  name_en: string;
  name_ru: string;
  slug: string;
}

function CategoryModal({ cats, onClose, onSaved }: { cats: Category[]; onClose: () => void; onSaved: () => void }) {
  const [list, setList] = useState<Category[]>(cats);
  const [newName, setNewName] = useState('');
  const [newNameEn, setNewNameEn] = useState('');
  const [newNameRu, setNewNameRu] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name_zh: '', name_en: '', name_ru: '', slug: '' });
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [deletingCat, setDeletingCat] = useState<number | null>(null);
  const [err, setErr] = useState('');
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; catName: string; catId: number }>({ open: false, catName: '', catId: 0 });

  useEffect(() => { setList(cats); }, [cats]);

  const handleAdd = async () => {
    if (!newName || !newSlug) { setErr('分类名和slug不能为空'); return; }
    setSaving(true);
    const { error } = await supabase.from('article_categories').insert({ name_zh: newName, name_en: newNameEn, name_ru: newNameRu, slug: newSlug, sort_order: list.length });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setNewName(''); setNewNameEn(''); setNewNameRu(''); setNewSlug(''); setErr('');
    onSaved();
  };

  const startEdit = (c: Category) => {
    setEditingId(c.id);
    setEditForm({ name_zh: c.name_zh, name_en: c.name_en || '', name_ru: c.name_ru || '', slug: c.slug });
    setErr('');
  };

  const cancelEdit = () => { setEditingId(null); setErr(''); };

  const handleSaveEdit = async () => {
    if (!editForm.name_zh || !editForm.slug) { setErr('分类名和slug不能为空'); return; }
    setSaving(true);
    const { error } = await supabase.from('article_categories').update(editForm).eq('id', editingId);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setEditingId(null);
    onSaved();
  };

  const moveCat = async (index: number, direction: number) => {
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    const newList = [...list];
    [newList[index], newList[target]] = [newList[target], newList[index]];
    setList(newList);
    setReordering(true);
    for (let i = 0; i < newList.length; i++) {
      await supabase.from('article_categories').update({ sort_order: i }).eq('id', newList[i].id);
    }
    setReordering(false);
    onSaved();
  };

  const handleDeleteCat = async () => {
    const { catId } = confirmModal;
    setDeletingCat(catId);
    const { error } = await supabase.from('article_categories').delete().eq('id', catId);
    setDeletingCat(null);
    setConfirmModal({ open: false, catName: '', catId: 0 });
    if (error) { setErr('删除失败：' + error.message); return; }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="font-bold text-[#1a3a5c]">分类管理</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
        </div>
        <div className="p-6 overflow-y-auto">
          <div className="space-y-2 mb-5">
            {list.map((c, idx) => (
              <div key={c.id} className="px-3 py-2 bg-[#f8f5f0] rounded-xl">
                {editingId === c.id ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-400">编辑分类</span>
                      <button onClick={cancelEdit} className="ml-auto text-xs text-gray-400 hover:text-gray-600 cursor-pointer">取消</button>
                    </div>
                    <input type="text" value={editForm.name_zh} onChange={(e) => setEditForm({ ...editForm, name_zh: e.target.value })} placeholder="中文名称（必填）" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]" />
                    <input type="text" value={editForm.name_en} onChange={(e) => setEditForm({ ...editForm, name_en: e.target.value })} placeholder="英文名称" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]" />
                    <input type="text" value={editForm.name_ru} onChange={(e) => setEditForm({ ...editForm, name_ru: e.target.value })} placeholder="俄文名称" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]" />
                    <input type="text" value={editForm.slug} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value.replace(/\s/g, '-').toLowerCase() })} placeholder="URL标识符（如：medical）" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]" />
                    <button onClick={handleSaveEdit} disabled={saving} className="w-full bg-[#1a3a5c] text-white font-semibold py-2 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
                      {saving ? '保存中...' : '保存修改'}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <i className="ri-folder-line text-[#c9a96e] text-sm shrink-0"></i>
                    <span className="text-sm font-medium text-[#1a3a5c] flex-1 truncate">{c.name_zh}</span>
                    {c.name_en && <span className="text-xs text-gray-400 truncate">/ {c.name_en}</span>}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => moveCat(idx, -1)}
                        disabled={idx === 0 || reordering}
                        className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-[#c9a96e] hover:bg-[#f0e8d8] cursor-pointer transition-colors disabled:opacity-30"
                        title="上移"
                      >
                        <i className="ri-arrow-up-s-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => moveCat(idx, 1)}
                        disabled={idx === list.length - 1 || reordering}
                        className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-[#c9a96e] hover:bg-[#f0e8d8] cursor-pointer transition-colors disabled:opacity-30"
                        title="下移"
                      >
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => startEdit(c)}
                        className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-[#c9a96e] hover:bg-[#f0e8d8] cursor-pointer transition-colors"
                        title="编辑分类"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => setConfirmModal({ open: true, catName: c.name_zh, catId: c.id })}
                        disabled={deletingCat === c.id}
                        className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors disabled:opacity-50"
                        title="删除分类"
                      >
                        <i className={`text-sm ${deletingCat === c.id ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'}`}></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <p className="text-xs font-medium text-gray-400 uppercase">添加新分类</p>
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="中文名称（必填）" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]" />
            <input type="text" value={newNameEn} onChange={(e) => setNewNameEn(e.target.value)} placeholder="英文名称" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]" />
            <input type="text" value={newNameRu} onChange={(e) => setNewNameRu(e.target.value)} placeholder="俄文名称" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]" />
            <input type="text" value={newSlug} onChange={(e) => setNewSlug(e.target.value.replace(/\s/g, '-').toLowerCase())} placeholder="URL标识符（如：medical）" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]" />
            {err && <p className="text-red-500 text-xs">{err}</p>}
            <button onClick={handleAdd} disabled={saving} className="w-full bg-[#1a3a5c] text-white font-semibold py-2.5 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? '保存中...' : '添加分类'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmModal.open}
        title="删除分类"
        message={`确认删除分类「${confirmModal.catName}」？该分类下的文章将变为无分类状态，此操作不可恢复。`}
        confirmText="确认删除"
        danger
        onConfirm={handleDeleteCat}
        onCancel={() => setConfirmModal({ open: false, catName: '', catId: 0 })}
      />
    </div>
  );
}

export default function ArticlesPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');
  const [filterTab, setFilterTab] = useState<'all' | 'published' | 'scheduled' | 'draft'>('all');
  const [filterFeatured, setFilterFeatured] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusCounts, setStatusCounts] = useState<{ published: number; scheduled: number; draft: number }>({ published: 0, scheduled: 0, draft: 0 });
  const [showCatModal, setShowCatModal] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; danger?: boolean; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Batch selection
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [catRes, artRes] = await Promise.all([
      supabase.from('article_categories').select('*').order('sort_order'),
      supabase.from('articles').select('*, article_categories(name_zh)').neq('status', 'deleted').order('published_at', { ascending: false, nullsFirst: false }),
    ]);
    setCategories(catRes.data ?? []);
    const rawArts = (artRes.data ?? []) as Article[];
    const _nowTs = Date.now();
    setStatusCounts({
      published: rawArts.filter((a) => a.status === 'published' && !(a.scheduled_at && new Date(a.scheduled_at).getTime() > _nowTs)).length,
      scheduled: rawArts.filter((a) => a.status === 'published' && a.scheduled_at && new Date(a.scheduled_at).getTime() > _nowTs).length,
      draft: rawArts.filter((a) => a.status === 'draft').length,
    });
    let arts = rawArts;
    const nowTs = Date.now();
    if (filterTab === 'published') {
      arts = arts.filter((a) => a.status === 'published' && !(a.scheduled_at && new Date(a.scheduled_at).getTime() > nowTs));
    } else if (filterTab === 'scheduled') {
      arts = arts.filter((a) => a.status === 'published' && a.scheduled_at && new Date(a.scheduled_at).getTime() > nowTs);
    } else if (filterTab === 'draft') {
      arts = arts.filter((a) => a.status === 'draft');
    }
    if (filterCat) arts = arts.filter((a) => a.category_id === parseInt(filterCat));
    if (filterFeatured === 'yes') arts = arts.filter((a) => a.featured === true);
    if (filterFeatured === 'no') arts = arts.filter((a) => a.featured !== true);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      arts = arts.filter((a) => {
        return (
          (a.title_zh && a.title_zh.toLowerCase().includes(q)) ||
          (a.title_en && a.title_en.toLowerCase().includes(q)) ||
          (a.article_categories?.name_zh && a.article_categories.name_zh.toLowerCase().includes(q)) ||
          (Array.isArray(a.tags) && a.tags.some((t: string) => t.toLowerCase().includes(q)))
        );
      });
    }
    setArticles(arts);
    setSelectedIds([]);
    setCurrentPage(1);
    setLoading(false);
  }, [filterCat, filterTab, filterFeatured, searchQuery]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totalPages = Math.max(1, Math.ceil(articles.length / pageSize));
  const paginated = articles.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const allPageSelected = paginated.length > 0 && paginated.every((a) => selectedIds.includes(a.id));

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pageIds = paginated.map((a) => a.id);
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...selectedIds, ...pageIds]));
      setSelectedIds(merged);
    }
  };

  const handleDelete = async (id: number) => {
    setConfirmModal({
      open: true,
      title: '删除文章',
      message: '确认删除该文章？此操作不可恢复。',
      danger: true,
      onConfirm: async () => {
        setDeleting(id);
        const { error } = await supabase.from('articles').update({ status: 'deleted' }).eq('id', id);
        setDeleting(null);
        setConfirmModal((p) => ({ ...p, open: false }));
        if (error) {
          setToast({ msg: '删除失败：' + error.message, type: 'error' });
        } else {
          setToast({ msg: '删除成功', type: 'success' });
          fetchAll();
        }
      },
    });
  };

  const handleBatchDelete = async () => {
    if (!selectedIds.length) return;
    setConfirmModal({
      open: true,
      title: '批量删除文章',
      message: `确认批量删除选中的 ${selectedIds.length} 篇文章？此操作不可恢复。`,
      danger: true,
      onConfirm: async () => {
        const { error } = await supabase.from('articles').update({ status: 'deleted' }).in('id', selectedIds);
        if (error) {
          setToast({ msg: '批量删除失败：' + error.message, type: 'error' });
        } else {
          setToast({ msg: `成功删除 ${selectedIds.length} 篇文章`, type: 'success' });
          setSelectedIds([]);
          fetchAll();
        }
        setConfirmModal((p) => ({ ...p, open: false }));
      },
    });
  };

  const handleBatchPublish = async () => {
    if (!selectedIds.length) return;
    const { error } = await supabase.from('articles').update({ status: 'published', published_at: new Date().toISOString(), scheduled_at: null }).in('id', selectedIds);
    if (error) {
      setToast({ msg: '批量发布失败：' + error.message, type: 'error' });
    } else {
      setToast({ msg: `成功发布 ${selectedIds.length} 篇文章`, type: 'success' });
      setSelectedIds([]);
      fetchAll();
    }
  };

  const handleBatchDraft = async () => {
    if (!selectedIds.length) return;
    const { error } = await supabase.from('articles').update({ status: 'draft', scheduled_at: null }).in('id', selectedIds);
    if (error) {
      setToast({ msg: '批量下架失败：' + error.message, type: 'error' });
    } else {
      setToast({ msg: `成功下架 ${selectedIds.length} 篇文章`, type: 'success' });
      setSelectedIds([]);
      fetchAll();
    }
  };

  const handleToggleStatus = async (a: Article) => {
    const newStatus = a.status === 'published' ? 'draft' : 'published';
    await supabase.from('articles').update({
      status: newStatus,
      published_at: newStatus === 'published' ? new Date().toISOString() : null,
      scheduled_at: null,
      updated_at: new Date().toISOString(),
    }).eq('id', a.id);
    fetchAll();
  };

  const isScheduledArt = (a: Article) => a.status === 'published' && !!a.scheduled_at && new Date(a.scheduled_at).getTime() > Date.now();
  const statusLabel = (a: Article) => (a.status === 'draft' ? '草稿' : isScheduledArt(a) ? '定时' : '已发');

  const statusTabList = [
    { key: 'all', label: '全部文章', show: true },
    { key: 'published', label: '已发', show: statusCounts.published > 0 },
    { key: 'scheduled', label: '定时', show: statusCounts.scheduled > 0 },
    { key: 'draft', label: '草稿', show: statusCounts.draft > 0 },
  ].filter((t) => t.show);
  const showStatusTabs = statusCounts.published + statusCounts.scheduled + statusCounts.draft > 1;

  const handleToggleFeatured = async (a: Article) => {
    const nextFeatured = !a.featured;
    await supabase.from('articles').update({
      featured: nextFeatured,
      sort_order: nextFeatured ? (a.sort_order ?? 5) : a.sort_order,
    }).eq('id', a.id);
    fetchAll();
  };

  const handleSetWeight = async (a: Article, weight: number) => {
    await supabase.from('articles').update({ sort_order: weight }).eq('id', a.id);
    fetchAll();
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">文章管理</h1>
          <p className="text-gray-400 text-sm mt-1">管理行业科普文章，支持多语言</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowCatModal(true)} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-4 py-2 rounded-full text-sm cursor-pointer hover:border-[#c9a96e] transition-colors whitespace-nowrap">
            <i className="ri-folder-settings-line"></i>分类管理
          </button>
          <Link to="/admin/articles/new" className="flex items-center gap-1.5 bg-[#c9a96e] hover:bg-[#b8944f] text-white px-4 py-2 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-add-line"></i>发布文章
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 mb-5 space-y-3">
        {/* Status tabs */}
        {showStatusTabs && (
          <div className="flex flex-wrap gap-1 bg-[#f8f5f0] rounded-full p-1 w-fit">
            {statusTabList.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilterTab(tab.key as 'all' | 'published' | 'scheduled' | 'draft')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
                  filterTab === tab.key ? 'bg-white text-[#1a3a5c] shadow-sm' : 'text-gray-500 hover:text-[#1a3a5c]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Filters in one row */}
        <div className="flex flex-wrap gap-3 items-center">
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 cursor-pointer focus:outline-none focus:border-[#c9a96e] bg-white">
            <option value="">全部分类</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name_zh}</option>)}
          </select>
          <select value={filterFeatured} onChange={(e) => setFilterFeatured(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 cursor-pointer focus:outline-none focus:border-[#c9a96e] bg-white">
            <option value="">全部状态</option>
            <option value="yes">首页推荐</option>
            <option value="no">未推荐</option>
          </select>
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white max-w-xs">
            <i className="ri-search-line text-gray-400 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索标题 / 标签 / 分类..."
              className="bg-transparent text-sm text-gray-600 outline-none flex-1"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-circle-fill text-sm"></i>
              </button>
            )}
          </div>
          <div className="ml-auto text-xs text-gray-400">{articles.length} 篇文章</div>
        </div>
      </div>

      {/* Batch action bar */}
      {selectedIds.length > 0 && (
        <div className="bg-[#1a3a5c] rounded-2xl px-5 py-3 mb-4 flex items-center gap-3 flex-wrap">
          <span className="text-white text-sm font-medium">已选中 {selectedIds.length} 项</span>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={handleBatchPublish} className="px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-check-line mr-1"></i>批量发布
            </button>
            <button onClick={handleBatchDraft} className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-draft-line mr-1"></i>批量下架
            </button>
            <button onClick={handleBatchDelete} className="px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-delete-bin-line mr-1"></i>批量删除
            </button>
            <button onClick={() => setSelectedIds([])} className="text-white/60 hover:text-white text-xs cursor-pointer ml-2">
              清除选择
            </button>
          </div>
        </div>
      )}

      {/* Articles list */}
      <div className="bg-white rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm"><i className="ri-loader-4-line text-2xl animate-spin block mb-2"></i>加载中...</div>
        ) : articles.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <i className="ri-article-line text-4xl mb-3 block"></i>
            <p className="text-sm mb-4">暂无文章</p>
            <Link to="/admin/articles/new" className="inline-flex items-center gap-1 bg-[#c9a96e] text-white px-4 py-2 rounded-full text-sm cursor-pointer whitespace-nowrap">
              <i className="ri-add-line"></i>写第一篇文章
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f8f5f0]">
                <tr className="text-gray-500 text-xs">
                  <th className="px-5 py-3.5 w-10">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-[#c9a96e] focus:ring-[#c9a96e] cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-5 py-3.5 font-medium">标题</th>
                  <th className="text-left px-5 py-3.5 font-medium">分类</th>
                  <th className="text-left px-5 py-3.5 font-medium">状态</th>
                  <th className="text-left px-5 py-3.5 font-medium">首页推荐</th>
                  <th className="text-left px-5 py-3.5 font-medium">发布时间</th>
                  <th className="text-left px-5 py-3.5 font-medium">修改时间</th>
                  <th className="text-left px-5 py-3.5 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginated.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(a.id)}
                        onChange={() => toggleSelect(a.id)}
                        className="w-4 h-4 rounded border-gray-300 text-[#c9a96e] focus:ring-[#c9a96e] cursor-pointer"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {a.cover_img && (
                          <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
                            <img src={a.cover_img} alt={a.title_zh} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-[#1a3a5c] line-clamp-1">{a.title_zh}</div>
                          {a.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {a.tags.slice(0, 3).map((t) => (
                                <span key={t} className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">#{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-500">{a.article_categories?.name_zh || '无分类'}</td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => handleToggleStatus(a)}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium cursor-pointer transition-all ${
                          a.status === 'published' ? 'bg-green-50 text-green-600 border border-green-200 hover:bg-red-50 hover:text-red-500' : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-600'
                        }`}
                        title={a.status === 'published' ? '点击下架' : '点击发布'}
                      >
                        {statusLabel(a)}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleFeatured(a)}
                          title={a.featured ? '点击取消首页推荐' : '点击设为首页推荐'}
                          className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium cursor-pointer transition-all whitespace-nowrap ${
                            a.featured ? 'bg-[#c9a96e]/15 text-[#b8944f] border border-[#c9a96e]/30' : 'bg-gray-100 text-gray-400 hover:bg-[#c9a96e]/10'
                          }`}
                        >
                          {a.featured ? <i className="ri-star-fill text-xs"></i> : <i className="ri-star-line text-xs"></i>}
                          {a.featured ? '推荐中' : '未推荐'}
                        </button>
                        {a.featured && (
                          <select
                            value={a.sort_order ?? 5}
                            onChange={(e) => handleSetWeight(a, parseInt(e.target.value))}
                            title="推荐权重：数字越小越靠前"
                            className="border border-gray-200 rounded-md px-1.5 py-0.5 text-xs text-gray-600 cursor-pointer focus:outline-none focus:border-[#c9a96e] bg-white"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                              <option key={n} value={n}>权重 {n}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-400 text-xs">
                      {a.published_at ? new Date(a.published_at).toLocaleDateString('zh-CN') : '—'}
                    </td>
                    <td className="px-5 py-4 text-gray-400 text-xs">
                      {a.updated_at ? new Date(a.updated_at).toLocaleDateString('zh-CN') : '—'}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {a.status !== 'draft' && (
                          <a href={`${basePath}/news/${a.id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-[#c9a96e] cursor-pointer">查看</a>
                        )}
                        <Link to={`/admin/articles/${a.id}`} className="text-xs text-[#c9a96e] hover:underline cursor-pointer">编辑</Link>
                        <button onClick={() => handleDelete(a.id)} disabled={deleting === a.id} className="text-xs text-gray-400 hover:text-red-500 cursor-pointer disabled:opacity-50">
                          {deleting === a.id ? '删除中...' : '删除'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={articles.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>

      {showCatModal && (
        <CategoryModal
          cats={categories}
          onClose={() => setShowCatModal(false)}
          onSaved={fetchAll}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        danger={confirmModal.danger}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((p) => ({ ...p, open: false }))}
      />

      {/* Toast notification */}
      {toast && (
        <div className="fixed top-6 right-6 z-[60] px-5 py-3 rounded-xl shadow-lg text-sm font-medium transition-all animate-in fade-in slide-in-from-top-2">
          <div className={`${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'} px-4 py-3 rounded-xl flex items-center gap-2`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill text-green-500' : 'ri-error-warning-fill text-red-500'}`}></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}